import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { serviceReportsApi, customersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/components/SimpleToast";
import { MobileFab } from "@/components/MobileFab";
import { TableList } from "@/components/data-table/TableList";
import { StatusBadge } from "@/components/data-table/StatusBadge";
import { EmptyState } from "@/components/ListStates";
import { Plus, FileText, Building2, User, Eye, Trash2, Download, Mail, Send, Clock, CheckCircle2 } from "lucide-react";

const fmt = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); };

export default function ServiceReports() {
  const navigate = useNavigate();
  const toast = useToast();
  const [refreshSignal, setRefresh] = useState(0);
  const reload = () => setRefresh((n) => n + 1);
  const [downloading, setDownloading] = useState(null);
  const [emailReport, setEmailReport] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: "", cc: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const fetcher = (params) => serviceReportsApi.getPaged(params).then((r) => r.data);

  const handleDownloadPdf = async (report) => {
    setDownloading(report.id);
    try {
      const response = await serviceReportsApi.getPdf(report.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `servicebericht-${report.id.substring(0, 8)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch { toast.error("Fehler beim PDF-Download"); } finally { setDownloading(null); }
  };

  const handleOpenEmail = async (report) => {
    let customerEmail = "";
    try { if (report.customer_id) { const res = await customersApi.getById(report.customer_id); customerEmail = res.data?.email || ""; } }
    catch (err) { console.debug("Kunden-E-Mail:", err); }
    setEmailForm({
      to: customerEmail, cc: "", subject: `Servicebericht: ${report.title || ""}`,
      message: `Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie den Servicebericht "${report.title || ""}"${report.customer_name ? ` für ${report.customer_name}` : ""}.\n\nFreundliche Grüße\nOliver Rudolph`,
    });
    setEmailReport(report);
  };

  const handleSendEmail = async () => {
    if (!emailForm.to) { toast.error("Bitte eine Empfänger-E-Mail angeben"); return; }
    setSending(true);
    try {
      await serviceReportsApi.sendEmail(emailReport.id, { to_email: emailForm.to, cc_email: emailForm.cc || null, subject: emailForm.subject || null, message: emailForm.message || null });
      toast.success(`Servicebericht an ${emailForm.to} gesendet`); setEmailReport(null); reload();
    } catch (error) { toast.error(error?.response?.data?.detail || "E-Mail-Versand fehlgeschlagen"); } finally { setSending(false); }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Servicebericht „${report.title}" wirklich löschen?`)) return;
    try { await serviceReportsApi.delete(report.id); toast.success("Servicebericht gelöscht"); reload(); }
    catch { toast.error("Fehler beim Löschen"); }
  };

  const columns = [
    { key: "title", header: "Titel", sortable: true, render: (r) => (
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-warning/10 text-warning flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></div>
          <span className="font-medium truncate">{r.title}</span>
        </div>
      ) },
    { key: "customer_name", header: "Kunde", sortable: true, render: (r) => r.customer_name ? <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-muted-foreground" />{r.customer_name}</span> : <span className="text-muted-foreground">—</span> },
    { key: "technician_name", header: "Techniker", sortable: true, render: (r) => <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-muted-foreground" />{r.technician_name}</span> },
    { key: "service_date", header: "Datum", sortable: true, render: (r) => fmt(r.service_date) },
    { key: "work_hours", header: "Arbeit", render: (r) => <span className="inline-flex items-center gap-1 text-sm"><Clock className="w-3.5 h-3.5 text-muted-foreground" />{r.work_hours || 0}h</span> },
    { key: "status", header: "Status", render: (r) => r.signed_at
        ? <StatusBadge status="erledigt" label="Signiert" tone="success" icon={CheckCircle2} />
        : <StatusBadge status="offen" label="Offen" /> },
    { key: "email", header: "E-Mail", render: (r) => r.email_sent_at
        ? <StatusBadge status="aktiv" label={`Gesendet ${fmt(r.email_sent_at)}`} tone="info" icon={Mail} /> : <span className="text-muted-foreground">—</span> },
  ];

  const rowActions = (r) => [
    { label: "Öffnen", icon: Eye, onClick: () => navigate(`/service-reports/${r.id}/edit`) },
    { label: "PDF", icon: Download, onClick: () => handleDownloadPdf(r) },
    { label: "E-Mail", icon: Mail, onClick: () => handleOpenEmail(r) },
    { label: "Löschen", icon: Trash2, danger: true, separatorBefore: true, onClick: () => handleDelete(r) },
  ];

  const bulkActions = [
    { label: "Löschen", icon: Trash2, danger: true, onClick: async (ids) => {
        if (!window.confirm(`${ids.length} Serviceberichte wirklich löschen?`)) throw new Error("cancel");
        await serviceReportsApi.bulkDelete(ids); toast.success(`${ids.length} Berichte gelöscht`);
      } },
  ];

  return (
    <div className="space-y-5 animate-fade-in" data-testid="service-reports-page">
      <MobileFab onClick={() => navigate("/service-reports/new")} label="Neuer Bericht" testid="fab-new-report" />
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Serviceberichte</h1>
          <p className="text-muted-foreground text-sm mt-1">Erstellen und verwalten Sie Serviceberichte</p>
        </div>
        <Button asChild size="sm" data-testid="new-report-btn">
          <Link to="/service-reports/new"><Plus className="w-4 h-4 mr-2" />Neuer Bericht</Link>
        </Button>
      </div>

      <TableList
        tableKey="service_reports"
        columns={columns}
        fetcher={fetcher}
        requiredColumns={["title"]}
        defaultColumns={["title", "customer_name", "technician_name", "service_date", "work_hours", "status"]}
        defaultSort={{ by: "created_at", dir: "desc" }}
        defaultFilters={{ signed: "all" }}
        filterControls={[{ key: "signed", defaultLabel: "Signatur", width: 150, options: [
          { value: "all", label: "Signatur: alle" }, { value: "yes", label: "Signiert" }, { value: "no", label: "Nicht signiert" }] }]}
        chipLabel={(k, v) => (k === "signed" ? `Signatur: ${v === "yes" ? "signiert" : "offen"}` : null)}
        searchPlaceholder="Titel, Kunde oder Techniker…"
        rowActions={rowActions}
        bulkActions={bulkActions}
        onRowClick={(r) => navigate(`/service-reports/${r.id}/edit`)}
        refreshSignal={refreshSignal}
        testid="reports-list"
        mobileCard={(r) => (
          <div>
            <p className="font-semibold truncate">{r.title || "Servicebericht"}</p>
            <div className="mt-1 text-sm text-muted-foreground space-y-0.5">
              {r.customer_name && <p className="inline-flex items-center gap-1.5 truncate"><Building2 className="w-3.5 h-3.5" />{r.customer_name}</p>}
              <p className="inline-flex items-center gap-1.5 truncate"><User className="w-3.5 h-3.5" />{r.technician_name}</p>
              <p className="inline-flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{r.work_hours || 0}h</span>
                <span>{fmt(r.service_date)}</span>
              </p>
            </div>
            <div className="mt-2">
              {r.signed_at
                ? <StatusBadge status="aktiv" label="Signiert" tone="success" />
                : <StatusBadge status="offen" label="Offen" tone="warning" />}
            </div>
          </div>
        )}
        emptyState={
          <EmptyState
            icon={FileText}
            title="Keine Serviceberichte gefunden"
            description="Passen Sie Suche/Filter an oder erstellen Sie einen neuen Bericht."
            action={<Button asChild><Link to="/service-reports/new"><Plus className="w-4 h-4 mr-2" />Ersten Bericht erstellen</Link></Button>}
          />
        }
      />

      <Dialog open={!!emailReport} onOpenChange={(o) => !o && setEmailReport(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="email-report-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-primary" />Servicebericht per E-Mail senden</DialogTitle><DialogDescription>Der Bericht wird als PDF an die angegebene E-Mail-Adresse gesendet.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="email-to">Empfänger *</Label><Input id="email-to" type="email" placeholder="kunde@beispiel.de" value={emailForm.to} onChange={(e) => setEmailForm((x) => ({ ...x, to: e.target.value }))} data-testid="email-to-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="email-cc">Kopie (CC, optional)</Label><Input id="email-cc" type="email" placeholder="kopie@beispiel.de" value={emailForm.cc} onChange={(e) => setEmailForm((x) => ({ ...x, cc: e.target.value }))} data-testid="email-cc-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="email-subject">Betreff</Label><Input id="email-subject" value={emailForm.subject} onChange={(e) => setEmailForm((x) => ({ ...x, subject: e.target.value }))} data-testid="email-subject-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="email-message">Nachricht</Label><Textarea id="email-message" rows={6} value={emailForm.message} onChange={(e) => setEmailForm((x) => ({ ...x, message: e.target.value }))} data-testid="email-message-input" /></div>
            <p className="text-xs text-muted-foreground">Der Servicebericht wird als PDF angehängt; die R-NetWork-Signatur wird automatisch ergänzt.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailReport(null)} data-testid="email-cancel-btn">Abbrechen</Button>
            <Button onClick={handleSendEmail} disabled={sending} data-testid="email-send-btn">{sending ? <div className="spinner w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}Senden</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
